import { useState, useRef, useEffect, useCallback } from 'react';
import { sendChatMessage } from '../services/chatService';
import type { Message } from '../types/assets';
import { v4 as uuidv4 } from 'uuid';
import { getCookie, setCookie } from '../utils/cookieUtils';
import { CONTACT } from '../content/site';

// Shown when the chat API is not configured or cannot be reached. It says so
// plainly instead of imitating an assistant, and gives the direct contacts.
const OFFLINE_GREETING = `Hello, and welcome to EFSoft. Our chat assistant is offline right now, so the quickest way to reach us is ${CONTACT.phoneDisplay} or ${CONTACT.email}.`;
const OFFLINE_REPLY = `Our chat assistant is offline right now, so we could not answer that here. Call us on ${CONTACT.phoneDisplay} or email ${CONTACT.email} and we will reply.`;

/** "Not configured" is the expected state in local development, so it is not logged as an error. */
const logChatFailure = (context: string, err: unknown): void => {
  if (err instanceof Error && err.message === 'CHAT_API_NOT_CONFIGURED') {
    console.info(`${context}: chat API not configured (VITE_API_BASE_URL), showing the offline message.`);
  } else {
    console.error(`${context}:`, err);
  }
};

interface UseChatProps {
  chatId?: string;
  maxMessages?: number;
}

interface UseChatReturn {
  messages: Message[];
  sendMessage: (text: string) => Promise<void>;
  isLoading: boolean;
  error: string | null;
}

export const useChat = ({ chatId, maxMessages = 10 }: UseChatProps = {}): UseChatReturn => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const initialMessageSent = useRef(false);

  // Generate senderId and manage cookie
  const [senderId] = useState<string>(() => {
    const existingSenderId = getCookie('senderId');
    if (existingSenderId) {
      return existingSenderId;
    } else {
      const newSenderId = uuidv4();
      setCookie('senderId', newSenderId, { days: 365 });
      return newSenderId;
    }
  });

  // Send initial message to get greeting from AI
  useEffect(() => {
    const sendInitialMessage = async () => {
      if (!initialMessageSent.current) {
        initialMessageSent.current = true;
        try {
          const botResponse = await sendChatMessage(chatId, [], senderId);
          if (botResponse.trim() !== '') {
            setMessages([{ text: botResponse, isUser: false, id: uuidv4(), timestamp: new Date() }]);
          }
        } catch (err: unknown) {
          logChatFailure('Error fetching initial message', err);
          setMessages([{ text: OFFLINE_GREETING, isUser: false, id: uuidv4(), timestamp: new Date() }]);
        }
      }
    };

    sendInitialMessage();
  }, [chatId, senderId]);

  // Add a new user message and get bot response
  const sendMessage = useCallback(async (messageText: string): Promise<void> => {
    setError(null);
    setIsLoading(true);
    const trimmedMessage = messageText.trim();

    // --- Handle initial empty message --- 
    if (trimmedMessage === '') {
      try {
        const botResponse = await sendChatMessage(chatId, [], senderId); // Send empty array
        // Use functional update to avoid race condition if initial message is already set
        setMessages(prevMessages => {
          if (botResponse.trim() !== '' && prevMessages.length === 0) {
            return [{ text: botResponse, isUser: false, id: uuidv4(), timestamp: new Date() }];
          }
          return prevMessages; // No change needed
        });
      } catch (err: unknown) {
        logChatFailure('Error fetching initial message', err);
        setMessages([{ text: OFFLINE_GREETING, isUser: false, id: uuidv4(), timestamp: new Date() }]);
      } finally {
        setIsLoading(false);
      }
      return;
    }

    // --- Handle regular message sending --- 
    const userMessage: Message = { text: trimmedMessage, isUser: true, id: uuidv4(), timestamp: new Date() };

    // Prepare history: New message + text of previous 2 messages
    const previousMessagesTexts = messages
      .slice(-2) // Get last 2 messages from current state
      .map(m => m.text).reverse();
      
    // Construct the history array: [newMessage, prev1, prev2]
    const history = [trimmedMessage, ...previousMessagesTexts];
    
    // Pad with empty strings up to 3 elements if needed
    // (Backend expects 3 elements: new message + up to 2 previous)
    while (history.length < 3) {
        history.push(''); 
    }

    // Add user message to the state immediately (optimistic update)
    // Use functional update for safety
    setMessages(prevMessages => {
        const newMessages = [...prevMessages, userMessage];
        // Enforce maxMessages limit
        if (newMessages.length > maxMessages) {
            return newMessages.slice(-maxMessages);
        }
        return newMessages;
    });

    try {
      // Send to API with the prepared history
      const botResponseText = await sendChatMessage(chatId, history, senderId);

      // Update state with bot response using functional update
      setMessages(prevMessages => {
          const botMessage: Message | null = botResponseText.trim() !== ''
              ? { text: botResponseText, isUser: false, id: uuidv4(), timestamp: new Date() }
              : null;

          if (botMessage) {
              const finalMessages = [...prevMessages, botMessage];
              // Enforce maxMessages limit again after adding bot response
              if (finalMessages.length > maxMessages) {
                 return finalMessages.slice(-maxMessages);
              }
              return finalMessages;
          } 
          // If bot response is empty, return previous state (which already includes user message)
          return prevMessages; 
      });

    } catch (error: unknown) {
      if (error instanceof Error && error.message === 'CONTENT_FILTER_ERROR') {
        setError('Our content filter blocked this message. Try asking your question in a different way.');
      } else {
        logChatFailure('Error in sendMessage', error);
        setMessages(prevMessages => {
          const botMessage: Message = { text: OFFLINE_REPLY, isUser: false, id: uuidv4(), timestamp: new Date() };
          return [...prevMessages, botMessage].slice(-maxMessages);
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [messages, chatId, senderId, maxMessages]);

  return {
    messages,
    sendMessage,
    isLoading,
    error
  };
};
